import { useEffect, useRef, useState } from 'react';
import { cameraConstraints, recorderOptions, stopTracks } from '../lib/localCamera.mjs';

export function LocalCameraTools({ onChooseFile }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const requestRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const [ready, setReady] = useState(false);
  const [opening, setOpening] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audio, setAudio] = useState(false);
  const [facing, setFacing] = useState('user');
  const [aspect, setAspect] = useState(16 / 9);
  const [clip, setClip] = useState(null);
  const [status, setStatus] = useState('Camera is off. Recordings stay on this device; AI has not inspected them.');

  function closeCamera() {
    requestRef.current += 1;
    clearTimeout(timerRef.current);
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    stopTracks(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setReady(false);
    setOpening(false);
  }
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      clearTimeout(timerRef.current);
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      stopTracks(streamRef.current);
    };
  }, []);
  useEffect(() => () => { if (clip?.url) URL.revokeObjectURL(clip.url); }, [clip]);
  useEffect(() => {
    if (ready && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => setStatus('Tap the video play control to start the preview.'));
    }
  }, [ready]);

  async function openCamera() {
    if (opening || recording) return;
    closeCamera();
    const request = ++requestRef.current;
    setOpening(true);
    setStatus('Allow OmniMark camera access. Microphone is requested only if you enabled recording audio.');
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('unsupported');
      const stream = await navigator.mediaDevices.getUserMedia(cameraConstraints(facing, audio));
      if (!mountedRef.current || request !== requestRef.current) { stopTracks(stream); return; }
      streamRef.current = stream;
      setReady(true);
      setStatus('Camera ready. Native framing is preserved—no cropping or digital zoom.');
    } catch (error) {
      if (mountedRef.current && request === requestRef.current) {
        setStatus(error.name === 'NotAllowedError' ? 'Camera or microphone permission was denied. Allow it in your browser, or choose an existing clip.' : 'Camera could not start. Use an HTTPS browser with a camera, or choose an existing clip.');
      }
    } finally {
      if (mountedRef.current && request === requestRef.current) setOpening(false);
    }
  }

  function record() {
    if (!ready || recording) return;
    if (!window.MediaRecorder) { setStatus('Recording is not supported here. Use Choose existing clip to open your device camera.'); return; }
    const chunks = [];
    try {
      const recorder = new window.MediaRecorder(streamRef.current, recorderOptions(window.MediaRecorder));
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data.size) chunks.push(event.data); };
      recorder.onstop = () => {
        clearTimeout(timerRef.current);
        if (!mountedRef.current) return;
        setRecording(false);
        closeCamera();
        const blob = new Blob(chunks, { type: recorder.mimeType || chunks[0]?.type || 'video/webm' });
        if (!blob.size) { setStatus('No video was captured. Please try again.'); return; }
        setClip({ url: URL.createObjectURL(blob), extension: blob.type.includes('mp4') ? 'mp4' : 'webm', size: blob.size });
        setStatus('Recording ready for local playback and download. Nothing was uploaded or sent to AI.');
      };
      recorder.onerror = () => {
        closeCamera();
        setRecording(false);
        setStatus('Recording failed. Choose an existing clip instead.');
      };
      recorder.start();
      setRecording(true);
      setStatus('Recording locally. Stop when finished; recording stops automatically after 30 seconds.');
      timerRef.current = setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 30000);
    } catch (_) { setStatus('This device cannot record that camera format. Choose an existing clip instead.'); }
  }
  const button = 'rounded-lg border border-white/25 px-3 py-2 text-sm disabled:opacity-50';
  return <section aria-label="OmniMark local camera tools" className="m-3 rounded-xl border border-white/20 p-3">
    <h3 className="font-semibold">Camera workspace</h3>
    <p className="my-2 text-xs">For full-body references, keep head, both hands, feet, and floor visible. These tools capture media; they do not certify avatar quality.</p>
    <div className="flex flex-wrap items-center gap-2">
      <label className="text-sm">Camera <select aria-label="Camera facing" disabled={ready || opening} value={facing} onChange={(event) => setFacing(event.target.value)} className="rounded p-2 text-slate-950"><option value="user">Front</option><option value="environment">Rear</option></select></label>
      <label className="text-sm"><input type="checkbox" checked={audio} disabled={ready || opening} onChange={(event) => setAudio(event.target.checked)} /> Record microphone audio</label>
      <button type="button" className={button} disabled={ready || opening} onClick={openCamera}>{opening ? 'Opening…' : 'Start camera'}</button>
      <button type="button" className={button} onClick={() => { closeCamera(); setStatus('Camera is off.'); }} disabled={!ready && !opening}>Close camera</button>
      <button type="button" className={button} disabled={!ready || recording} onClick={record}>Record clip</button>
      <button type="button" className={button} disabled={!recording} onClick={() => recorderRef.current?.stop()}>Stop recording</button>
      <button type="button" className={button} onClick={onChooseFile}>Choose existing clip</button>
    </div>
    {ready && <video ref={videoRef} autoPlay muted playsInline controls onLoadedMetadata={(event) => { const video = event.currentTarget; if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight); }} style={{ width: `min(100%, ${60 * aspect}svh)`, aspectRatio: aspect, objectFit: 'contain', marginInline: 'auto' }} className="mt-3 rounded-lg bg-black" />}
    {clip && <div className="mt-3"><video src={clip.url} playsInline controls className="max-h-[60svh] w-full rounded-lg bg-black object-contain" /><a className="mt-2 inline-block underline" href={clip.url} download={`omnimark-camera-reference.${clip.extension}`}>Download clip ({(clip.size / 1024 / 1024).toFixed(1)} MB)</a><button type="button" className={`${button} ml-3`} onClick={() => setClip(null)}>Discard clip</button></div>}
    <p role="status" className="mt-3 text-xs">{status}</p>
  </section>;
}
